import {
  RegisterInput,
  RegisterInputSchema,
  AuthUser,
  AuthUserSchema,
  LoginInput,
  LoginInputSchema,
  DeleteAccountInputSchema
} from '@/contracts/schemas';
import { storage } from './storage';

const AUTH_STORAGE_KEY = 'zaporca_auth_user';
const ACCOUNTS_KEY = 'zaporca_local_accounts';

type LocalAccount = AuthUser & { passwordHash: string };

const memoryAccounts = new Map<string, LocalAccount>();

function accountKey(email: string): string {
  return email.trim().toLowerCase();
}

async function hashPassword(email: string, password: string): Promise<string> {
  const data = new TextEncoder().encode(`${accountKey(email)}:${password}`);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function loadAccounts(): Map<string, LocalAccount> {
  if (!isLocalStorageAvailable()) return memoryAccounts;
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return memoryAccounts;
    const parsed = JSON.parse(raw) as LocalAccount[];
    memoryAccounts.clear();
    for (const item of parsed) {
      if (item?.email && item.passwordHash) memoryAccounts.set(accountKey(item.email), item);
    }
  } catch {
    /* mantém o que já está na memória */
  }
  return memoryAccounts;
}

function saveAccounts(): void {
  if (!isLocalStorageAvailable()) return;
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify([...memoryAccounts.values()]));
}

export function clearLocalAccounts(): void {
  memoryAccounts.clear();
  if (isLocalStorageAvailable()) localStorage.removeItem(ACCOUNTS_KEY);
}

let memoryUser: AuthUser | null = null;
const authSubscribers: Array<(user: AuthUser | null) => void> = [];

function isLocalStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function getCurrentUser(): AuthUser | null {
  if (!isLocalStorageAvailable()) return memoryUser;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return AuthUserSchema.parse(parsed);
  } catch {
    return null;
  }
}

export function setCurrentUser(user: AuthUser | null): void {
  memoryUser = user;
  if (isLocalStorageAvailable()) {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }
  notifyAuthSubscribers(user);
}

export function onAuthChange(callback: (user: AuthUser | null) => void): () => void {
  authSubscribers.push(callback);
  callback(getCurrentUser());
  return () => {
    const index = authSubscribers.indexOf(callback);
    if (index >= 0) authSubscribers.splice(index, 1);
  };
}

function notifyAuthSubscribers(user: AuthUser | null): void {
  authSubscribers.forEach(cb => {
    try {
      cb(user);
    } catch (err) {
      console.error('Erro no listener de autenticacao:', err);
    }
  });
}

/**
 * Vincula dados criados localmente (perfil MEI e orcamentos) a conta de usuario recem-criada.
 */
export function linkLocalDataToUser(userId: string, nome?: string, email?: string): void {
  // 1. Atualiza Perfil MEI com os dados do usuario
  const perfilAtual = storage.getPerfilMei();
  const perfilAtualizado = {
    ...perfilAtual,
    nome: nome || perfilAtual.nome,
    email: email || perfilAtual.email
  };
  storage.savePerfilMei(perfilAtualizado);

  // 2. Garante que todos os orcamentos locais estejam enfileirados no Outbox para a conta
  const orcamentos = storage.getOrcamentos();
  orcamentos.forEach(orc => {
    storage.enqueueOutbox('UPSERT_ORCAMENTO', orc.uuid, {
      ...orc,
      userId
    });
  });
}

/**
 * Registra uma nova conta com consentimento explicito LGPD (Lei 13.709/2018).
 */
export async function registerAccount(
  input: RegisterInput
): Promise<{ success: boolean; user?: AuthUser; warning?: string; error?: string }> {
  // 1. Validacao compulsória de schema Zod (inclui aceiteLgpd === true)
  const validated = RegisterInputSchema.parse(input);
  const accounts = loadAccounts();
  const key = accountKey(validated.email);
  if (accounts.has(key)) {
    return {
      success: false,
      error: 'Já existe uma conta com esse e-mail neste aparelho. Entre com a senha.'
    };
  }

  const consentTimestamp = new Date().toISOString();
  const lgpdVersion = '1.0';

  const authUser: AuthUser = {
    id: `local-user-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    email: validated.email,
    nome: validated.nomeCompleto,
    lgpdConsentAt: consentTimestamp,
    lgpdVersion
  };

  accounts.set(key, { ...authUser, passwordHash: await hashPassword(key, validated.password) });
  saveAccounts();
  setCurrentUser(authUser);
  linkLocalDataToUser(authUser.id, authUser.nome, authUser.email);

  return {
    success: true,
    user: authUser,
    warning: 'Modo Offline: Conta criada e dados vinculados localmente com sucesso.'
  };
}

/**
 * Desconecta o usuario atual.
 */
export async function logout(): Promise<void> {
  setCurrentUser(null);
}

/**
 * Realiza login do usuário com e-mail e senha.
 * Suporta Supabase Auth quando configurado e fallback local resiliente.
 */
export async function loginAccount(
  input: LoginInput
): Promise<{ success: boolean; user?: AuthUser; warning?: string; error?: string }> {
  const validated = LoginInputSchema.parse(input);
  const accounts = loadAccounts();
  const key = accountKey(validated.email);
  const account = accounts.get(key);
  if (!account) {
    return {
      success: false,
      error: 'Não há conta com esse e-mail neste aparelho. Crie uma conta primeiro.'
    };
  }
  if ((await hashPassword(key, validated.password)) !== account.passwordHash) {
    return { success: false, error: 'A senha não confere com a conta deste aparelho.' };
  }

  const authUser: AuthUser = {
    id: account.id,
    email: account.email,
    nome: account.nome,
    lgpdConsentAt: account.lgpdConsentAt,
    lgpdVersion: account.lgpdVersion
  };

  setCurrentUser(authUser);

  return {
    success: true,
    user: authUser,
    warning: 'Modo Offline: Autenticado com sessão local persistente.'
  };
}

/**
 * Inicializa e restaura a sessão ativa de autenticação.
 */
export async function initAuthSession(): Promise<AuthUser | null> {
  return getCurrentUser();
}

/**
 * Realiza o descarte compulsório e definitivo de dados pessoais e operacionais (LGPD Art. 18, VI)
 * e encerra a conta do usuário no Supabase e no armazenamento local.
 * Exige confirmação estrita do e-mail cadastrado.
 */
export async function deleteAccountAndUserData(
  confirmEmail: string
): Promise<{ success: boolean; error?: string }> {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return { success: false, error: 'Nenhum usuário autenticado para exclusão.' };
  }

  // 1. Validação estrita via Zod do e-mail de confirmação
  const parseResult = DeleteAccountInputSchema.safeParse({
    confirmEmail,
    expectedEmail: currentUser.email
  });

  if (!parseResult.success) {
    const errorMsg = parseResult.error.issues[0]?.message || 'E-mail de confirmação inválido.';
    return { success: false, error: errorMsg };
  }

  // Descarte neste aparelho. Não há conta remota.
  storage.purgeAllUserData();
  loadAccounts().delete(accountKey(currentUser.email));
  saveAccounts();

  // 4. Limpeza da sessão ativa e notificação
  setCurrentUser(null);

  return { success: true };
}

